/**
 * /app/setup — Company onboarding setup pipeline
 *
 * Phased checklist that walks new admins through critical setup steps.
 * Auto-detects completion via cheap count queries on the backend, locks
 * dependent steps, supports skip-with-reason and manual-mark-complete,
 * celebrates milestones, and surfaces "Take me there" deep-links.
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell, { SectionCard } from "../components/AppShell";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Check, Lock, ArrowRight, SkipForward, Sparkles, Circle,
  Building2, Users, Wallet, Clock, Workflow, Layers,
} from "lucide-react";

// Phase metadata: icon + accent color (keep aligned with backend `phases`)
const PHASE_META = {
  "Foundation":            { icon: Building2, accent: "indigo" },
  "Org structure":         { icon: Layers,    accent: "violet" },
  "People":                { icon: Users,     accent: "rose"   },
  "Compliance & Payroll":  { icon: Wallet,    accent: "amber"  },
  "Time & Attendance":     { icon: Clock,     accent: "sky"    },
  "Workflows & Policies":  { icon: Workflow,  accent: "emerald"},
};
const ACCENT_BG = {
  indigo:  "bg-indigo-50 text-indigo-700  border-indigo-200",
  violet:  "bg-violet-50 text-violet-700  border-violet-200",
  rose:    "bg-rose-50   text-rose-700    border-rose-200",
  amber:   "bg-amber-50  text-amber-700   border-amber-200",
  sky:     "bg-sky-50    text-sky-700     border-sky-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const STATUS_PILL = {
  complete:     "bg-emerald-100 text-emerald-700",
  skipped:      "bg-zinc-100    text-zinc-500",
  not_started:  "bg-amber-100   text-amber-700",
};
const STATUS_LABEL = { complete: "Complete", skipped: "Skipped", not_started: "Pending" };

export default function Setup() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skipFor, setSkipFor] = useState(null);
  const [skipReason, setSkipReason] = useState("");
  const [showCelebration, setShowCelebration] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/setup/checklist");
      setData(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Admins only — setup is configured by company admins.");
        navigate("/app");
      } else {
        toast.error("Failed to load setup");
      }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Trigger confetti when crossing 100% (only once per session)
  useEffect(() => {
    if (data?.is_complete && !sessionStorage.getItem("setup_celebrated")) {
      sessionStorage.setItem("setup_celebrated", "1");
      setShowCelebration(true);
    }
  }, [data?.is_complete]);

  const handleSkip = async () => {
    if (!skipFor) return;
    try {
      await api.post("/setup/skip", { step_key: skipFor.key, reason: skipReason.trim() || "Not applicable" });
      toast.success("Step skipped");
      setSkipFor(null); setSkipReason("");
      load();
    } catch { toast.error("Could not skip"); }
  };

  const handleManualComplete = async (step) => {
    try {
      await api.post("/setup/complete", { step_key: step.key });
      toast.success(`"${step.title}" marked complete`);
      load();
    } catch { toast.error("Could not save"); }
  };

  const groupedSteps = useMemo(() => {
    if (!data) return [];
    const groups = {};
    for (const s of data.steps) (groups[s.phase] = groups[s.phase] || []).push(s);
    return Object.entries(groups);
  }, [data]);

  if (loading || !data) {
    return <AppShell title="Setup"><div className="text-sm text-zinc-500 py-8">Loading…</div></AppShell>;
  }

  return (
    <AppShell title="Setup">
      {/* Hero progress card */}
      <div
        className="relative rounded-2xl p-8 mb-6 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          color: "#fff",
        }}
        data-testid="setup-hero"
      >
        {/* Animated grid bg */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}/>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-xl">
            <div className="text-xs uppercase tracking-[0.18em] text-emerald-300 font-semibold mb-3">
              <Sparkles size={12} className="inline mr-1.5"/> Welcome to SaarOne
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="setup-h1">
              Let's get your company live.
            </h1>
            <p className="text-sm sm:text-base text-zinc-300 mt-3" data-testid="setup-sub">
              {data.is_complete
                ? "Every step is done. You're ready to run payroll, accept leaves, and onboard your team."
                : `Complete these ${data.total} steps to unlock payroll, attendance, and approvals. We'll auto-detect when you finish — no need to mark things off manually.`}
            </p>
          </div>

          {/* Ring progress + counts */}
          <div className="flex items-center gap-5">
            <ProgressRing progress={data.progress}/>
            <div className="text-right">
              <div className="text-4xl font-display font-bold" data-testid="setup-progress-num">{data.progress}%</div>
              <div className="text-xs text-zinc-300 mt-1">{data.done} of {data.total} steps</div>
              {data.completed_count > 0 && (
                <div className="text-[10px] text-emerald-300 mt-0.5">{data.completed_count} auto-detected</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Phase aggregates strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-6" data-testid="phase-strip">
        {Object.entries(data.phases).map(([phase, p]) => {
          const meta = PHASE_META[phase] || { icon: Circle, accent: "indigo" };
          const PhaseIcon = meta.icon;
          const pct = Math.round((p.done / p.total) * 100);
          return (
            <a href={`#phase-${slugify(phase)}`} key={phase}
               className={`p-3 rounded-lg border ${ACCENT_BG[meta.accent]} hover:scale-[1.02] transition-transform`}
               data-testid={`phase-pill-${slugify(phase)}`}>
              <PhaseIcon size={16} className="mb-1.5 opacity-80"/>
              <div className="text-xs font-semibold truncate">{phase}</div>
              <div className="text-[10px] mt-0.5 opacity-80">{p.done}/{p.total} · {pct}%</div>
            </a>
          );
        })}
      </div>

      {/* Phases */}
      <div className="space-y-6" data-testid="phase-list">
        {groupedSteps.map(([phase, steps]) => {
          const meta = PHASE_META[phase] || { icon: Circle, accent: "indigo" };
          const PhaseIcon = meta.icon;
          return (
            <SectionCard key={phase} testid={`section-phase-${slugify(phase)}`}>
              <a id={`phase-${slugify(phase)}`}/>
              <div className="flex items-center gap-3 mb-4 pb-3 border-b">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${ACCENT_BG[meta.accent]}`}>
                  <PhaseIcon size={18}/>
                </div>
                <div>
                  <div className="font-display font-semibold text-lg">{phase}</div>
                  <div className="text-xs text-zinc-500">{steps.filter(s => s.status === "complete" || s.status === "skipped").length} of {steps.length} done</div>
                </div>
              </div>

              <div className="space-y-2">
                {steps.map(step => (
                  <StepRow key={step.key} step={step}
                           onOpen={() => navigate(step.route)}
                           onSkip={() => setSkipFor(step)}
                           onManualComplete={() => handleManualComplete(step)}/>
                ))}
              </div>
            </SectionCard>
          );
        })}
      </div>

      {/* Skip dialog */}
      <Dialog open={!!skipFor} onOpenChange={(o) => { if (!o) { setSkipFor(null); setSkipReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="skip-dialog">
          <DialogHeader>
            <DialogTitle>Skip "{skipFor?.title}"?</DialogTitle>
            <DialogDescription>
              We'll record this as skipped. You can complete it any time from Settings → Setup audit.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Reason (optional)</Label>
            <Input data-testid="skip-reason"
                   value={skipReason}
                   onChange={(e) => setSkipReason(e.target.value)}
                   placeholder="e.g. We don't have field reps"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSkipFor(null); setSkipReason(""); }}>Cancel</Button>
            <Button onClick={handleSkip} data-testid="skip-confirm">Skip step</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Milestone celebration */}
      {showCelebration && (
        <CelebrationOverlay onClose={() => setShowCelebration(false)}/>
      )}
    </AppShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// StepRow
// ────────────────────────────────────────────────────────────────────────────
function StepRow({ step, onOpen, onSkip, onManualComplete }) {
  const isLocked = step.locked;
  const isDone = step.status === "complete" || step.status === "skipped";
  const isAutoDetectOnly = step.status === "not_started" && step.optional === false;
  return (
    <div
      className={`group flex items-start gap-4 p-4 rounded-lg border transition-all ${
        isLocked ? "bg-zinc-50 border-zinc-200 opacity-60"
        : isDone ? "bg-emerald-50/40 border-emerald-100"
        : "bg-white border-zinc-200 hover:border-zinc-300 hover:shadow-sm"
      }`}
      data-testid={`step-row-${step.key}`}
    >
      {/* Status icon */}
      <div className="flex-none mt-0.5">
        {step.status === "complete" ? (
          <div className="w-6 h-6 rounded-full bg-emerald-500 grid place-items-center">
            <Check size={14} className="text-white" strokeWidth={3}/>
          </div>
        ) : step.status === "skipped" ? (
          <div className="w-6 h-6 rounded-full bg-zinc-300 grid place-items-center">
            <SkipForward size={12} className="text-white"/>
          </div>
        ) : isLocked ? (
          <div className="w-6 h-6 rounded-full bg-zinc-200 grid place-items-center">
            <Lock size={12} className="text-zinc-500"/>
          </div>
        ) : (
          <Circle size={24} className="text-zinc-300"/>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-medium ${isDone ? "text-zinc-700" : "text-zinc-900"} ${isDone ? "" : ""}`}>{step.title}</span>
          <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${STATUS_PILL[step.status]}`}>
            {STATUS_LABEL[step.status]}
          </span>
          {step.optional && step.status === "not_started" && (
            <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Optional</span>
          )}
        </div>
        <div className="text-xs text-zinc-500 mt-1">{step.why}</div>
        {isLocked && step.blocked_by?.length > 0 && (
          <div className="text-[11px] text-amber-700 mt-1.5">
            <Lock size={10} className="inline mr-0.5"/>
            Locked — complete <b>{step.blocked_by.join(", ")}</b> first
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-none">
        {!isDone && !isLocked && (
          <>
            <Button size="sm" variant="ghost" onClick={onSkip}
                    className="h-8 text-zinc-500 hover:text-zinc-700"
                    data-testid={`step-skip-${step.key}`}>
              Skip
            </Button>
            {isAutoDetectOnly === false && step.optional && (
              <Button size="sm" variant="ghost" onClick={onManualComplete}
                      className="h-8 text-zinc-500 hover:text-emerald-700"
                      data-testid={`step-done-${step.key}`}>
                Mark done
              </Button>
            )}
            <Button size="sm" onClick={onOpen} className="h-8 gap-1.5"
                    data-testid={`step-open-${step.key}`}>
              Take me there <ArrowRight size={12}/>
            </Button>
          </>
        )}
        {isDone && (
          <Button size="sm" variant="outline" onClick={onOpen}
                  className="h-8 gap-1.5 opacity-70 group-hover:opacity-100"
                  data-testid={`step-review-${step.key}`}>
            Review
          </Button>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ProgressRing — animated SVG ring
// ────────────────────────────────────────────────────────────────────────────
function ProgressRing({ progress }) {
  const r = 36, c = 2 * Math.PI * r;
  const offset = c - (progress / 100) * c;
  return (
    <svg width={88} height={88} className="-rotate-90">
      <circle cx={44} cy={44} r={r} stroke="rgba(255,255,255,0.12)" strokeWidth="6" fill="none"/>
      <circle cx={44} cy={44} r={r} stroke="#34d399" strokeWidth="6" fill="none"
              strokeLinecap="round"
              strokeDasharray={c} strokeDashoffset={offset}
              style={{ transition: "stroke-dashoffset 800ms cubic-bezier(0.16, 1, 0.3, 1)" }}/>
    </svg>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Celebration overlay (100%)
// ────────────────────────────────────────────────────────────────────────────
function CelebrationOverlay({ onClose }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
         style={{ background: "rgba(15,23,42,0.55)" }}
         onClick={onClose}
         data-testid="celebration">
      {/* Confetti */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 60 }).map((_, i) => (
          <div key={i}
               className="absolute w-1.5 h-3 rounded-sm"
               style={{
                 left: `${Math.random() * 100}%`,
                 top: "-10px",
                 background: ["#34d399","#22d3ee","#fbbf24","#f472b6","#a78bfa"][i % 5],
                 animation: `confetti ${2 + Math.random() * 2}s cubic-bezier(0.16, 1, 0.3, 1) ${Math.random() * 1}s forwards`,
                 transform: `rotate(${Math.random() * 360}deg)`,
               }}/>
        ))}
      </div>
      <div className="relative bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="text-6xl mb-3">🎉</div>
        <h3 className="font-display text-2xl font-bold">You're all set!</h3>
        <p className="text-sm text-zinc-600 mt-2">
          Every setup step is complete. Your company can now run payroll, accept leaves, approve expenses, and onboard new team members.
        </p>
        <Button className="mt-6 w-full" onClick={onClose} data-testid="celebrate-close">
          Let's go →
        </Button>
      </div>
      <style>{`@keyframes confetti { to { transform: translateY(110vh) rotate(720deg); opacity: 0.7; } }`}</style>
    </div>
  );
}

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
